import {useCallback, useContext} from 'react';

import {BackgroundTaskContext} from './background-task-context';

import type {BackgroundTaskContextType, TaskConfig} from './typedef';
import type {ReactNode} from 'react';

export type BackgroundTask = {
    completeSuccess(message?: ReactNode): void,
    completeFailure(message?: ReactNode): void,
    onClose: () => void,
};

type CreateBackgroundTask = (
    type: string,
    message?: ReactNode,
    config?: TaskConfig
) => (null | BackgroundTask);

type UseBackgroundTaskManager = () => CreateBackgroundTask;

export const useBackgroundTaskManager: UseBackgroundTaskManager = () => {
    const taskManager =
        useContext<BackgroundTaskContextType>(BackgroundTaskContext);

    const createBackgroundTask: CreateBackgroundTask = useCallback(
        (taskType, message, config) => {
            const taskId = taskManager.createBackgroundTask({
                taskType,
                message,
                config,
            });

            if (!taskId) {
                return null;
            }

            const getCompleteTaskCallback = (success: boolean) =>
                (completeMessage?: ReactNode) =>
                    taskManager.completeBackgroundTask({
                        taskId,
                        success,
                        message: completeMessage,
                    });

            const onClose = () => {
                taskManager.completeBackgroundTask({
                    taskId,
                    success: true,
                    message: undefined,
                });
            };

            return {
                completeSuccess: getCompleteTaskCallback(true),
                completeFailure: getCompleteTaskCallback(false),
                onClose,
            };
        },
        [taskManager],
    );

    return createBackgroundTask;
};
